import React from 'react';
import { Box, Typography, Tooltip } from "@mui/material";
import { styled } from '@mui/material/styles';
import { tooltipClasses } from '@mui/material/Tooltip';
import CategoryItem from './CategoryItem';
import { colors } from '../../utils/themeUtils';

// Custom styled tooltip to match the app's tooltip style
const StyledTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 'none',
    whiteSpace: 'nowrap',
    backgroundColor: 'rgba(250, 250, 252, 0.98)',
    padding: '10px 14px',
    fontSize: '0.85rem',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    border: '1px solid rgba(0, 0, 0, 0.06)',
    color: '#333',
    '&.MuiTooltip-tooltipPlacementTop': {
      marginBottom: '6px'
    }
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: 'rgba(250, 250, 252, 0.98)'
  }
});

/**
 * Component to render the Quick Overview section in the dashboard
 */
const QuickOverview = ({
  timeframe,
  sortOption,
  view,
  overallStats,
  categoryStats,
  getTimeframeDays,
  formatCurrency,
  formatImpact,
  isCategoryHighlighted,
  handleCategoryClick,
  currencySymbol,
  selectedBusiness
}) => {
  // Helper function to get category impact values
  const getCategoryImpactValues = (categoryName) => {
    const category = categoryStats.topCategories.find(
      cat => cat.name.toLowerCase() === categoryName.toLowerCase()
    );
    
    if (!category) return { positive: 0, negative: 0 };
    
    let positiveImpact = category.positive;
    let negativeImpact = Math.abs(category.negative);
    
    // Apply revenue multiplier if in sales view
    if (view === "Sales" && selectedBusiness?.turnover) {
      const multiplier = selectedBusiness.turnover * 0.1;
      positiveImpact *= multiplier;
      negativeImpact *= multiplier;
    }
    
    return { 
      positive: positiveImpact, 
      negative: negativeImpact 
    };
  };
  
  // Helper function to render tooltip content
  const getTooltipContent = (categoryName) => {
    const { positive, negative } = getCategoryImpactValues(categoryName);
    
    return (
      <Box sx={{ p: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="body2">
            Positive: <span style={{ color: colors.positiveGreen, fontWeight: 'bold' }}>
              +{view === "Sales" 
                ? formatCurrency(positive)
                : formatImpact(positive)}
            </span>
          </Typography>
          <Typography variant="body2">
            Negative: <span style={{ color: colors.negativeRed, fontWeight: 'bold' }}>
              -{view === "Sales" 
                ? formatCurrency(negative)
                : formatImpact(negative)}
            </span>
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflow: 'auto'
    }}>
      {/* Overview text container */}
      <Box>
        <Typography variant="body1" sx={{ fontSize: '1.125rem', lineHeight: 1.7, mt: 1, mb: 1 }}>
          {timeframe === "all" ? "Over" : "In the"}{' '}
          <Box component="span" sx={{ fontWeight: 'bold', color: '#000000DE' }}>
            {getTimeframeDays()}
          </Box>
          {', '}
          {sortOption !== 'negative' && (
            <>
              positive review comments have impacted your{' '}
              <Box component="span" sx={{ fontWeight: 'bold', color: '#000000DE' }}>
                {view === "Sales" ? "revenue" : "rating"}
              </Box>
              {' '}by{' '}
              <Box component="span" sx={{ color: colors.positiveGreen, fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                {view === "Sales" 
                  ? `+${formatCurrency(overallStats.positiveRevenueImpact || 0)}`
                  : `+${formatImpact(overallStats.positiveImpact)}`}
              </Box>
            </>
          )}
          
          {sortOption !== 'positive' && (
            <Box sx={{ display: 'inline' }}>
              <Box component="span">
                {sortOption === 'total' 
                  ? ' while negative comments have impacted it by ' 
                  : sortOption === 'negative' 
                    ? <>
                        negative review comments have impacted your{' '}
                        <Box component="span" sx={{ fontWeight: 'bold', color: '#000000DE' }}>
                          {view === "Sales" ? "revenue" : "rating"}
                        </Box>
                        {' '}by{' '}
                      </>
                    : 'Negative review comments have impacted it by '}
              </Box>
              <Box component="span" sx={{ color: colors.negativeRed, fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                {view === "Sales" 
                  ? `-${formatCurrency(Math.abs(overallStats.negativeRevenueImpact || 0))}`
                  : `-${formatImpact(Math.abs(overallStats.negativeImpact))}`}
              </Box>
            </Box>
          )}
          .
        </Typography>

        <Typography variant="body1" sx={{ lineHeight: 1.7, fontSize: '1.125rem' }}>
          <Box component="span" sx={{ color: '#000000DE' }}>
            Filter reviews by{' '}
            <StyledTooltip 
              title={getTooltipContent('Product')}
              arrow
              placement="top"
            >
              <Box 
                component="span" 
                className="category-name"
                sx={{ 
                  fontWeight: 'bold', 
                  cursor: 'pointer',
                  textDecoration: isCategoryHighlighted('Product') ? 'underline' : 'none',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }} 
                onClick={() => handleCategoryClick('Product')}
              >Product</Box>
            </StyledTooltip>{', '}
            <StyledTooltip 
              title={getTooltipContent('Service')}
              arrow
              placement="top"
            >
              <Box 
                component="span" 
                className="category-name"
                sx={{ 
                  fontWeight: 'bold', 
                  cursor: 'pointer',
                  textDecoration: isCategoryHighlighted('Service') ? 'underline' : 'none',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }} 
                onClick={() => handleCategoryClick('Service')}
              >Service</Box>
            </StyledTooltip>{' or '}
            <StyledTooltip 
              title={getTooltipContent('Experience')}
              arrow
              placement="top"
            >
              <Box 
                component="span" 
                className="category-name"
                sx={{ 
                  fontWeight: 'bold', 
                  cursor: 'pointer',
                  textDecoration: isCategoryHighlighted('Experience') ? 'underline' : 'none',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }} 
                onClick={() => handleCategoryClick('Experience')}
              >Experience</Box>
            </StyledTooltip> to see more specific insights.
          </Box>
        </Typography>
      </Box>
    </Box>
  );
};

export default QuickOverview; 